import { useState } from "react";
import { toast } from "sonner";
import { Send, Search, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Panel } from "@/components/admin/AdminLayout";

const STEPS = [
  "In Telegram, open @BotFather → send /newbot → pick a name → copy the token and save it in the Telegram box below.",
  "Telegram app → New Channel → give it a name (e.g. BachatRaja Deals) → choose Public → pick a link like bachatrajadeals.",
  "Open the channel → Administrators → Add Admin → search your bot's @username → keep 'Post messages' ON → Save.",
  "Press 'Find my channel' below (fills it in for you), or type the channel's @name in the Telegram box.",
  "Press 'Check setup'. When all ticks are green, press 'Send test post' and look at your channel.",
];

const Btn = ({ onClick, busy, icon: Icon, children, testid, primary }) => (
  <button disabled={busy} onClick={onClick} data-testid={testid}
    className={`px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 disabled:opacity-40 ${primary ? "bg-sky-600 text-white" : "border border-slate-300 bg-white"}`}>
    <Icon size={15} /> {children}
  </button>
);

export const TelegramSetup = ({ onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e), { duration: 9000 }); } finally { setBusy(false); } };
  const check = () => run(async () => setResult((await api.post("/admin/telegram/check")).data));
  const find = () => run(async () => {
    const d = (await api.post("/admin/telegram/find-channel")).data;
    toast.success(`Found channel "${d.title}" (${d.channel_id}) and saved it`); onChanged(); setResult((await api.post("/admin/telegram/check")).data);
  });
  const test = () => run(async () => { await api.post("/admin/social/test"); toast.success("Test post sent - check your channel"); });
  return (
    <Panel title="Telegram channel setup - step by step" testid="telegram-setup">
      <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-700" data-testid="telegram-setup-steps">
        {STEPS.map((s) => <li key={s}>{s}</li>)}
      </ol>
      <div className="flex flex-wrap gap-2 mt-4">
        <Btn onClick={find} busy={busy} icon={Search} testid="telegram-find-channel-button">Find my channel</Btn>
        <Btn onClick={check} busy={busy} icon={ShieldCheck} testid="telegram-check-button">Check setup</Btn>
        <Btn onClick={test} busy={busy || !result?.ok} icon={Send} testid="telegram-test-post-button" primary>Send test post</Btn>
      </div>
      {result && (
        <ul className="mt-4 space-y-1.5" data-testid="telegram-check-result">
          {result.steps.map((s) => (
            <li key={s.key} className="text-sm flex gap-2" data-testid={`telegram-step-${s.key}`}>
              {s.ok ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" /> : <XCircle size={16} className="text-red-600 shrink-0 mt-0.5" />}
              <span><b>{s.label}</b>{s.detail && <span className="text-slate-600"> - {s.detail}</span>}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};
